import React, { useState, useEffect, useRef } from 'react';

const API_URL = process.env.REACT_APP_API_URL;

const MessageCenter = ({ onSend, templates = [] }) => {
  const messageRef = useRef(null);
  const [message, setMessage] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState(null);
  const [channels, setChannels] = useState({
    sms: true,
    whatsapp: false
  });

  // Écouter l'événement de mise à jour du numéro de téléphone
  useEffect(() => {
    const handlePhoneNumber = (event) => {
      console.log('Événement reçu:', event.detail);
      if (event.detail && event.detail.phoneNumber) {
        setPhoneNumber(event.detail.phoneNumber);
        // Faire défiler jusqu'au centre de messagerie
        messageRef.current?.scrollIntoView({ behavior: 'smooth' });
      }
    };

    window.addEventListener('setPhoneNumber', handlePhoneNumber);
    return () => {
      window.removeEventListener('setPhoneNumber', handlePhoneNumber);
    };
  }, []);

  const handleTemplateChange = (e) => {
    const template = templates.find(t => t.id === e.target.value);
    setSelectedTemplate(e.target.value);
    if (template) {
      setMessage(template.content);
    }
  };

  const showNotification = (msg, type = 'info') => {
    setNotification({ message: msg, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const handleSend = async (e) => {
    e?.preventDefault();
    if (!phoneNumber) {
      setError('Veuillez entrer un numéro de téléphone');
      return;
    }
    if (!message) {
      setError('Veuillez entrer un message');
      return;
    }
    if (!channels.sms && !channels.whatsapp) {
      setError('Veuillez sélectionner au moins un canal de communication');
      return;
    }

    setSending(true);
    setError(null);

    try {
      const selectedChannels = Object.entries(channels)
        .filter(([_, enabled]) => enabled)
        .map(([channel]) => channel);

      if (onSend) {
        await onSend(phoneNumber, message, selectedChannels);
      } else {
        // Utiliser la route appropriée selon que c'est un template ou un message personnalisé
        const endpoint = selectedTemplate === 'custom' || !selectedTemplate
          ? `${API_URL}/api/custom-messages/send`
          : `${API_URL}/api/messages/send`;

        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            to: phoneNumber,
            message,
            channels: selectedChannels
          }),
        });

        const data = await response.json();
        if (!data.success) {
          throw new Error(data.error || 'Erreur lors de l\'envoi du message');
        }
      }

      showNotification('Message envoyé avec succès!', 'success');
      setMessage('');
      setSelectedTemplate('');
      // On garde le numéro de téléphone pour pouvoir envoyer plusieurs messages
    } catch (error) {
      console.error('Erreur:', error);
      setError(error.message);
      showNotification(error.message, 'error');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-lg max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold mb-6">Centre de Messagerie</h2>
      
      {notification && (
        <div className={`p-4 rounded mb-4 ${
          notification.type === 'error' ? 'bg-red-100 text-red-700' : 
          notification.type === 'success' ? 'bg-green-100 text-green-700' : 
          'bg-blue-100 text-blue-700'
        }`}>
          {notification.message}
        </div>
      )}

      <form onSubmit={handleSend} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Numéro de téléphone
          </label>
          <input
            type="tel"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            placeholder="+1234567890"
            className="w-full p-2 border rounded focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Template
          </label>
          <select
            value={selectedTemplate}
            onChange={handleTemplateChange}
            className="w-full p-2 border rounded focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Message personnalisé</option>
            {templates.map(template => (
              <option key={template.id} value={template.id}>
                {template.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Canaux de communication
          </label>
          <div className="flex space-x-4">
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                checked={channels.sms}
                onChange={() => setChannels(prev => ({ ...prev, sms: !prev.sms }))}
                className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
              />
              <span className="ml-2">SMS</span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                checked={channels.whatsapp}
                onChange={() => setChannels(prev => ({ ...prev, whatsapp: !prev.whatsapp }))}
                className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
              />
              <span className="ml-2">WhatsApp</span>
            </label>
          </div>
        </div>

        <div ref={messageRef}>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Message
          </label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows="4"
            className="w-full p-2 border rounded focus:ring-blue-500 focus:border-blue-500"
            placeholder="Entrez votre message..."
          />
        </div>

        {error && (
          <div className="text-red-600 text-sm">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={sending || !phoneNumber || !message || (!channels.sms && !channels.whatsapp)}
          className={`w-full py-2 px-4 rounded ${
            sending || !phoneNumber || !message || (!channels.sms && !channels.whatsapp)
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700'
          } text-white font-semibold`}
        >
          {sending ? 'Envoi en cours...' : 'Envoyer'}
        </button>
      </form>
    </div>
  );
};

export default MessageCenter;
